import { Injectable } from '@angular/core';
import { DatabaseService } from '@nsk-app/services/database.service';
import { ConfigurationService } from '@nsk-app/services/configuration.service';
import { LocalNotifications, LocalNotificationSchema } from '@capacitor/local-notifications';
import { ActionPerformed, PushNotifications } from '@capacitor/push-notifications';
import { addDays, addSeconds } from 'date-fns';
import { NGXLogger } from 'ngx-logger';
import { merge } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  private pushToken: string = null;
  private action: ActionPerformed = null;

  constructor(
    private router: Router,
    private database: DatabaseService,
    private configuration: ConfigurationService,
    private logger: NGXLogger
  ) {

    merge(this.database.newDataObservable,this.configuration.dataChangedObservable)
      .subscribe(async () => {
      try{
        await this.syncNotifications();
        this.logger.info('Synchronized Notifications.');
      }catch (e){
        this.logger.warn('Could not update Notifications!');
      }
    });
  }

  public get token() : string | null {
    return this.pushToken;
  }

  public get hasAction(): boolean {
    return this.action !== null;
  }

  public async initialize(): Promise<void> {
    await this.getPermissions();
  }

  public async getPermissions(): Promise<boolean> {
    let perm = await LocalNotifications.checkPermissions();
    if(perm.display !== 'granted'){
      perm = await LocalNotifications.requestPermissions();
    }

    if(Capacitor.getPlatform() !== 'web'){
      let pushPerm = await PushNotifications.checkPermissions();
      if(pushPerm.receive !== 'granted'){
        pushPerm = await PushNotifications.requestPermissions();
      }

      if(pushPerm.receive === 'granted'){
        await this.refreshToken();
      }else{
        this.logger.fatal('No permission for APN',pushPerm);
      }
    }


    if(perm.display !== 'granted'){
      return false;
    }

    if(Capacitor.getPlatform() === 'android') {
      try{
        const {channels} = await LocalNotifications.listChannels();
        let channel = channels.find(x => x.id == 'nielsapp-local');

        if(!channel){
          await LocalNotifications.createChannel({
            id: 'nielsapp-local',
            name: 'Benachrichtigungen durch die Chronik',
            description: 'Erlaubt es der App, Sie über relevante Informationen zu Ihrer Operation zu informieren.',
            visibility: 0,
            lights: true,
            lightColor: '#B22052',
            vibration: true,
            importance: 3
          });
        }

      }catch (e){
        this.logger.info("Device does not support notification channels.");
      }

    }
  }

  public async syncNotifications() {
    if(!this.configuration.pushEnabled){
      return false;
    }

    const pushMoments = await this.database.getPushMoments();
    const days = this.configuration.daysUntilOperation;
    const date = new Date(this.configuration.operationDate);
    date.setHours(0, 0, 0, 0);

    this.logger.log({
      date,
      days,
      pushMoments
    });

    await this.removeNotifications();


    let notifications: LocalNotificationSchema[] = pushMoments.map(m => {

      const temp = document.createElement('div');
      temp.innerHTML = m.text;

      const text = (temp.textContent || temp.innerText || m.text);
      temp.remove();

      let offset = ((m.push_day !== null && m.push_day !== 1) ? m.push_day : 0);
      offset += (m.start_time !== null ? m.start_time : 0);
      const pushDate = addDays(date,  offset);

      if(m.push_time !== null){
        const time = m.push_time.split(':').map(x => parseInt(x, 10));

        pushDate.setHours(time[0], time[1], 0);
      }else{
        pushDate.setHours(8,0,0,0);
      }

      return {
        id: m.id,
        title: m.title,
        body: text.substr(0,80) + '...',
        largeBody: text,
        channelId: 'nielsapp-local',
        schedule: {
          at: pushDate,
          allowWhileIdle: true
        }
      };

    });

    notifications = notifications.filter(n => n.schedule.at > new Date());

    // notifications.push({
    //   id: -500,
    //   title: 'Test',
    //   body: 'Testosteron',
    //     schedule: { at: addSeconds(new Date(),30)}
    // });


    if(notifications.length > 0){
      const res = await LocalNotifications.schedule({notifications});
      this.logger.info('Notifications inserted.', [notifications, res.notifications]);
    }else {
      this.logger.info('Nothing to schedule');
    }


    return true;

  }

  public async removeNotifications() {
    const pending = await LocalNotifications.getPending();

    if(pending.notifications.length < 1){
      this.logger.info('No notification scheduled.', pending.notifications);
      return;
    }
    await LocalNotifications.cancel({notifications: pending.notifications});

    this.logger.info('Notifications deleted', pending.notifications);
  }

  public async refreshToken(): Promise<void> {

    const oldToken = await Preferences.get({key: 'push_token'});

    if(oldToken.value != null){
      this.pushToken = oldToken.value;
    }else{

      const registerEvent = (new Promise<string>(async (res,rej) => {
        PushNotifications.addListener('registration',(token) => {
          this.pushToken = token.value;
          res(token.value)
        });

        PushNotifications.addListener('registrationError',() => {
          rej('Could not register device.')
        });

        this.logger.info('Registering for Firebase...');
        await PushNotifications.register();
      }));


      try{
        this.pushToken = await registerEvent;
        this.logger.info('Success', this.pushToken);

        await Preferences.set({key: 'push_token', value: this.pushToken});

      }catch (e){
        this.logger.fatal('Error while registering',e);
      }
    }

    if(Capacitor.getPlatform() === 'android') {
      try{
        const {channels} = await PushNotifications.listChannels();
        let channel = channels.find(x => x.id == 'nielsapp-push');

        if(!channel){
          await PushNotifications.createChannel({
            id: 'nielsapp-push',
            name: 'Benachrichtigungen durch Ihr Krankenhaus',
            description: 'Erlaubt Ihnen wichtige Eilmeldungen Ihres Krankenhauses zu empfangen.',
            visibility: 1,
            lights: true,
            lightColor: '#B22052',
            vibration: true,
            importance: 5
          });
        }

      }catch (e) {
        this.logger.info("Device does not support notification channels.");
      }
    }


  }
}
